import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../../components/ui/Header';
import Footer from '../../homepage/components/Footer';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { sellerService } from '../../../services/sellerService';
import { productService } from '../../../services/productService'; 
import { sellerProductService } from '../../../services/sellerProductService'; 
import { cartService } from '../../../services/cartService'; // NEW: Import cartService
import { bundleService } from '../../../services/bundleService'; // NEW: Import bundleService
import ModelCard from '../../model-catalog/components/ModelCard';
import ViewControls from '../../model-catalog/components/ViewControls';
import ComponentListView from '../../model-catalog/components/ComponentListView';
import ComponentDetailModal from '../../model-catalog/components/ComponentDetailModal';
import BundlePreviewModal from '../../shopping-cart/components/BundlePreviewModal'; // NEW: Import BundlePreviewModal
import { componentService } from '../../../services/componentService';
import { catalogCardService } from '../../../services/catalogCardService';
import { imageUploadService } from '../../../utils/imageUploadService';
import { userService } from '../../../services/userService'; // Import userService
import LoginSignupModal from '../../../components/ui/LoginSignupModal'; // Import the modal
import { supabase } from '../../../lib/supabase';

const slugifyCategory = (text) => {
    if (!text) return '';
    return text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

const extractCategoryFromUrl = (catParam, searchParams) => {
    let raw = catParam || searchParams?.get('category') || searchParams?.get('holiday') || searchParams?.get('season') || '';
    if (raw) {
        raw = decodeURIComponent(raw).trim();
        if (raw.toLowerCase().startsWith('holiday=')) {
            raw = raw.slice(8);
        } else if (raw.toLowerCase().startsWith('season=')) {
            raw = raw.slice(7);
        } else if (raw.toLowerCase().startsWith('category=')) {
            raw = raw.slice(9);
        }
    }
    return raw;
};

const findMatchingCategory = (categories, param) => {
    if (!param || !categories || categories.length === 0) return '';
    let cleanParam = decodeURIComponent(param).trim();
    if (cleanParam.toLowerCase().startsWith('holiday=')) cleanParam = cleanParam.slice(8);
    else if (cleanParam.toLowerCase().startsWith('season=')) cleanParam = cleanParam.slice(7);
    else if (cleanParam.toLowerCase().startsWith('category=')) cleanParam = cleanParam.slice(9);

    const paramSlug = slugifyCategory(cleanParam);
    
    // 1. Exact match (case-insensitive)
    const exact = categories.find(c => c.toLowerCase() === cleanParam.toLowerCase());
    if (exact) return exact;

    // 2. Slug match (e.g. "toys" matches "Toys", "christmas" matches "Christmas")
    const slugMatch = categories.find(c => slugifyCategory(c) === paramSlug);
    if (slugMatch) return slugMatch;

    // 3. Substring / keyword match (e.g. "christmas" matches "Christmas Collection")
    const partialMatch = categories.find(c => {
        const cSlug = slugifyCategory(c);
        return paramSlug.includes(cSlug) || cSlug.includes(paramSlug);
    });
    if (partialMatch) return partialMatch;

    return cleanParam;
};

const PublicStorefront = () => {
    const { slug, category: categoryParam } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth(); // Get isAuthenticated

    const initialCategoryInput = extractCategoryFromUrl(categoryParam, searchParams);

    const [storefrontData, setStorefrontData] = useState(null);
    const [storeInactive, setStoreInactive] = useState(false);
    const [isStoreSuspended, setIsStoreSuspended] = useState(false); // Symmetrical state
    const [isStorefrontClosed, setIsStorefrontClosed] = useState(false); // Symmetrical state
    const [sellerProducts, setSellerProducts] = useState([]);
    const [sellerComponents, setSellerComponents] = useState([]);
    const [sellerBundles, setSellerBundles] = useState([]);
    const [selectedBundle, setSelectedBundle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingComponents, setLoadingComponents] = useState(false);
    const [error, setError] = useState('');
    const [storeNotFound, setStoreNotFound] = useState(false);
    const [activeCategory, setActiveCategory] = useState(initialCategoryInput);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAuthModal, setShowAuthModal] = useState(false); // Add modal state
    const [isWrapShopSeller, setIsWrapShopSeller] = useState(false);
    const [isRepairShopSeller, setIsRepairShopSeller] = useState(false);
    const [sellerWraps, setSellerWraps] = useState([]);

    // Sync activeCategory when URL params change or storefrontCategories finish loading
    useEffect(() => {
        const raw = extractCategoryFromUrl(categoryParam, searchParams);
        if (storefrontData?.storefrontCategories && storefrontData.storefrontCategories.length > 0) {
            if (raw) {
                const matched = findMatchingCategory(storefrontData.storefrontCategories, raw);
                setActiveCategory(matched || raw);
            } else {
                setActiveCategory('');
            }
        } else if (raw) {
            setActiveCategory(raw);
        }
    }, [categoryParam, searchParams, storefrontData?.storefrontCategories]);

    // Layout Controls
    const [viewMode, setViewMode] = useState('grid');
    const [sortBy, setSortBy] = useState('relevance');
    const [itemsPerPage, setItemsPerPage] = useState(24);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedModels, setSelectedModels] = useState([]);
    const [selectedDetailComponent, setSelectedDetailComponent] = useState(null);
    const [publishedLayout, setPublishedLayout] = useState(null);

    useEffect(() => {
        const fetchPublishedLayout = async () => {
            try {
                const layout = await catalogCardService.getPublished();
                if (layout && layout.config) {
                    setPublishedLayout(layout.config);
                }
            } catch (err) {
                console.error('Failed to load published catalog layout in storefront:', err);
            }
        };
        fetchPublishedLayout();
    }, []);

    const isPreview = searchParams.get('preview') === 'true';
    const canPreview = isPreview && user?.id === storefrontData?.userId;

    // Add modal handler
    const handleAuthRequired = () => {
        setShowAuthModal(true);
    };

    const handleAddToCart = async (itemIdOrObj, isComponent = false) => {
        if (!isAuthenticated) {
            handleAuthRequired();
            return;
        }
        try {
            const isObj = typeof itemIdOrObj === 'object' && itemIdOrObj !== null;
            
            // Symmetrical: If the component is customizable, redirect to the options selection modal!
            const is3D = isObj && (
                itemIdOrObj.is3dManufacturing === true || 
                itemIdOrObj.isCncCapable === true || 
                itemIdOrObj.isPainted === true
            );
            if (isComponent && is3D) {
                setSelectedDetailComponent(itemIdOrObj); // Opens the component options details modal!
                return;
            }

            const productId = isComponent ? null : (isObj ? itemIdOrObj.id : itemIdOrObj);
            const componentId = isComponent ? (isObj ? (itemIdOrObj.globalComponentId || itemIdOrObj.id) : itemIdOrObj) : null;
            const sellerId = storefrontData?.id || (isObj ? itemIdOrObj.sellerId : null);

            const payload = {
                userId: user?.id,
                productId,
                componentId,
                sellerId,
                quantity: 1
            };
            await cartService.addToCart(payload);
            alert('Item added to cart!');
        } catch (error) {
            console.error('Failed to add to cart:', error);
            alert(error.message || 'Failed to add item to cart');
        }
    };

    useEffect(() => {
        const fetchStorefrontData = async () => {
            setLoading(true);
            setError('');
            setStoreNotFound(false);
            setStoreInactive(false); // Reset to false on re-run!
            setIsStoreSuspended(false); // Reset to false on re-run!
            setIsStorefrontClosed(false); // Reset to false on re-run!
            try {
                let data = await sellerService.getPublicStorefrontData(slug);
                
                if (!data) {
                    setStoreNotFound(true);
                    return;
                }

                // DIRECT FAIL-SAFE CLIENT-SIDE CHECK (Bypasses any Edge Function caching/delay!)
                const { data: dbAccount } = await supabase
                    .from('seller_accounts')
                    .select('account_status')
                    .eq('id', data.id)
                    .maybeSingle();

                const finalStatus = dbAccount?.account_status || data.accountStatus;
                const isAuthorizedSeller = isPreview && user?.id === data.userId;

                const suspended = finalStatus && finalStatus.toLowerCase() !== 'active';
                const closed = data.storefrontActive === false;

                setIsStoreSuspended(suspended);
                setIsStorefrontClosed(closed);

                if ((suspended || closed) && !isAuthorizedSeller) {
                    console.log('Storefront is suspended or closed:', { finalStatus, storefrontActive: data.storefrontActive });
                    setStoreInactive(true);
                    setLoading(false);
                    return;
                }

                setStorefrontData(data);

                // Fetch roles symmetrically to determine if they are a Wrap or Repair Shop!
                let isWrap = false;
                let isRepair = false;
                if (data?.userId) {
                    try {
                        const { data: roleData } = await supabase
                            .from('user_roles')
                            .select('role:roles(name)')
                            .eq('user_id', data.userId);
                        if (roleData) {
                            const roles = roleData.map(r => r.role?.name);
                            isWrap = roles.includes('Wrap Shop');
                            isRepair = roles.includes('Repair Shop');
                        }
                    } catch (err) {
                        console.warn('Failed to load seller roles:', err);
                    }
                }
                setIsWrapShopSeller(isWrap);
                setIsRepairShopSeller(isRepair);

                if (isWrap && data?.userId) {
                    try {
                        const { data: wraps } = await supabase
                            .from('custom_wrap_configs')
                            .select('id, config_name, created_at, product:products(id, name, thumbnail_path)')
                            .eq('shop_id', data.userId);
                        setSellerWraps(wraps || []);
                    } catch (err) {
                        console.warn('Failed to load custom wrap configs:', err);
                    }
                }

                // RECORD STORE VISIT
                if (data.id && !isPreview) {
                    supabase?.rpc('increment_shop_views', { target_shop_id: data.id, shop_type: 'storefront' })?.then(undefined, err => {
                        console.error('Error auto-incrementing storefront views:', err);
                    });

                    userService.recordActivity(
                        user?.id || null, 
                        navigator.userAgent, 
                        'Store View', 
                        data.id, // Record the SELLER ID as the activity details
                        null
                    ).catch(e => console.warn('Store visit tracking error:', e));
                }
                
                const authorizedToPreview = isPreview && user?.id === data.userId;

                if (data && (data.storefrontActive || authorizedToPreview)) {
                    const fetchedSellerProducts = await sellerProductService.getPublicSellerProductsBySellerId(data.id);
                    
                    const bundlesData = await bundleService.getActiveBundles();
                    const filteredBundles = bundlesData.filter(b => b.seller_id === data.id);
                    setSellerBundles(filteredBundles);

                    const mappedProducts = (fetchedSellerProducts || []).map(p => ({
                        // Use p.productId as the main ID for ModelCard
                        id: p.productId, 
                        // Keep sellerProductId if needed for other operations (using spId from RPC)
                        sellerProductId: p.spId, 
                        // Map product-specific details from RPC (p_name -> pName, etc.)
                        name: p.pName,
                        description: p.description || p.pDescription, // Use seller description if available, fallback to global
                        categoryName: p.cName,
                        sku: p.pSku || p.stockCode,
                        manufacturerPartNumber: p.pUpc,
                        thumbnailPath: p.pThumbnailPath?.startsWith('http') ? p.pThumbnailPath : imageUploadService.getPublicUrl(p.pThumbnailPath, 'product-thumbnails'),
                        // Use seller's price (spPrice) for the display
                        price: p.spPrice, 
                        // Map other details ModelCard expects
                        sellerImageUrls: Array.isArray(p.sellerImageUrl) ? p.sellerImageUrl.filter(url => url !== null).map(url => url.startsWith('http') ? url : imageUploadService.getPublicUrl(url, 'seller-product-images')) : [],
                        stockQuantity: p.stockAmount, // Seller's stock quantity from RPC
                        viewsCount: p.viewsCount || 0, // Seller's live views count
                        averageRating: p.pAverageRating, // Average rating from product
                        totalRatings: p.pTotalRatings, // Total ratings from product
                        addedCount: p.pAddedCount, // Added count from product
                        componentCount: p.pComponentCount, // Component count from product
                        isFeatured: p.pIsFeatured, // Is featured from product
                        isCertified: p.pIsCertified, // Is certified from product
                        globalStaffpick: p.pGlobalStaffpick, // Global staff pick from product
                        staffpick: p.spStorePick, // Seller's custom store pick badge
                        // Assuming complexity, license, animated are not returned by this RPC for now.
                        // ModelCard will handle these as undefined/null.
                    }));
                    setSellerProducts(mappedProducts || []);
                } else {
                    setSellerProducts([]);
                }

            } catch (err) {
                console.error('fetchStorefrontData caught error:', err);
                if (err.message.includes('Storefront not found')) {
                    setStoreNotFound(true);
                } else {
                    setError(err.message || 'Failed to load storefront.');
                }
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchStorefrontData();
        } else {
            setError('No storefront specified.');
            setLoading(false);
        }
    }, [slug, isPreview, user?.id]);



    // Load components when viewMode changes to component-list
    useEffect(() => {
        if (viewMode === 'component-list' && sellerComponents.length === 0 && storefrontData?.id) {
            fetchSellerComponents();
        }
    }, [viewMode, storefrontData]);

    const fetchSellerComponents = async () => {
        setLoadingComponents(true);
        try {
            const components = await componentService.getAll({
                sellerId: storefrontData.id
            });

            // Process images
            const processed = (components || []).map((comp) => {
                if (comp.images && comp.images.length > 0) {
                    const publicUrls = comp.images.map((path) => {
                        if (!path) return null;
                        if (path.startsWith('http')) return path;
                        return imageUploadService.getPublicUrl(path); // Use service
                    });
                    return {
                        ...comp,
                        images: publicUrls.filter(url => url !== null)
                    };
                }
                return comp;
            });

            setSellerComponents(processed);
        } catch (err) {
            console.error('Failed to load components:', err);
            // Optionally set error state specific to components
        } finally {
            setLoadingComponents(false);
        }
    };

    const handleSelectModel = (modelId, checked) => {
        setSelectedModels((prev) =>
            checked ? [...prev, modelId] : prev.filter((id) => id !== modelId)
        );
    };

    const handleSelectAll = () => {
        const allIds = viewMode === 'component-list' 
            ? sellerComponents.map(c => c.id) 
            : sellerProducts.map(p => p.id);
            
        setSelectedModels((prev) =>
            prev.length === allIds.length ? [] : allIds
        );
    };

    const handleBulkAction = (action, ids) => {
        console.log(`Bulk action: ${action} on items:`, ids);
        // Implement bulk actions here
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCategorySelect = (category) => {
        setActiveCategory(category);
        setCurrentPage(1);
        if (!category) {
            navigate(`/store/${slug}`, { replace: false });
        } else {
            const catSlug = slugifyCategory(category);
            const holidayKeywords = ['christmas', 'halloween', 'thanksgiving', 'easter', 'valentines', 'new-year', '4th-of-july', 'holiday', 'holidays'];
            if (holidayKeywords.includes(catSlug)) {
                navigate(`/store/${slug}/holiday=${catSlug}`, { replace: false });
            } else {
                navigate(`/store/${slug}/${catSlug}`, { replace: false });
            }
        }
    };

    // Calculate derived state BEFORE early returns
    const getFilteredItems = (items, isProduct) => {
        return items.filter(item => {
            let matchesCategory = true;
            if (activeCategory) {
                const itemCategory = isProduct ? item.categoryName : (item.product?.category?.name || '');
                const holidayVal = (item.holiday_setting || item.holidaySetting || item.product?.holiday_setting || item.product?.holidaySetting || '').toLowerCase();
                const seasonVal = (item.seasonal_setting || item.seasonalSetting || item.product?.seasonal_setting || item.product?.seasonalSetting || '').toLowerCase();
                const keywordsVal = (item.keywords || item.product?.keywords || '').toLowerCase();
                const targetCat = activeCategory.toLowerCase();

                if (
                    itemCategory?.toLowerCase() === targetCat ||
                    holidayVal.includes(targetCat) ||
                    seasonVal.includes(targetCat) ||
                    keywordsVal.includes(targetCat)
                ) {
                    matchesCategory = true;
                } else {
                    const keywordsToMatch = new Set();
                    activeCategory.toLowerCase().split(/\s+/).filter(word => word.length > 0)
                        .forEach(word => {
                            keywordsToMatch.add(word);
                            if (word.endsWith('s')) {
                                keywordsToMatch.add(word.slice(0, -1));
                            } else {
                                keywordsToMatch.add(word + 's');
                            }
                        });

                    const itemName = (item.customName || item.name || '').toLowerCase();
                    const itemDesc = (item.description || '').toLowerCase();

                    matchesCategory = Array.from(keywordsToMatch).some(keyword =>
                        itemName.includes(keyword) ||
                        itemDesc.includes(keyword) ||
                        holidayVal.includes(keyword) ||
                        seasonVal.includes(keyword) ||
                        keywordsVal.includes(keyword)
                    );
                }
            }

            let matchesSearch = true;
            if (searchQuery) {
                const search = searchQuery.toLowerCase();
                const itemName = (item.customName || item.name || '').toLowerCase();
                const itemDesc = (item.description || '').toLowerCase();
                const itemMfgPart = (item.manufacturerPartNumber || item.sku || '').toLowerCase();
                matchesSearch = itemName.includes(search) || itemDesc.includes(search) || itemMfgPart.includes(search);
            }

            return matchesCategory && matchesSearch;
        });
    };

    const filteredProducts = getFilteredItems(sellerProducts, true);
    const filteredComponents = getFilteredItems(sellerComponents, false);
    const filteredBundles = sellerBundles.filter(item => {
        let matchesCategory = true;
        if (activeCategory) {
            const itemCat = (item.category || '').toLowerCase();
            const holidayVal = (item.holiday_setting || item.holidaySetting || '').toLowerCase();
            const seasonVal = (item.seasonal_setting || item.seasonalSetting || '').toLowerCase();
            const keywordsVal = (item.keywords || '').toLowerCase();
            const targetCat = activeCategory.toLowerCase();

            if (
                itemCat === targetCat ||
                holidayVal.includes(targetCat) ||
                seasonVal.includes(targetCat) ||
                keywordsVal.includes(targetCat)
            ) {
                matchesCategory = true;
            } else {
                const keywordsToMatch = new Set();
                activeCategory.toLowerCase().split(/\s+/).filter(word => word.length > 0)
                    .forEach(word => {
                        keywordsToMatch.add(word);
                        if (word.endsWith('s')) keywordsToMatch.add(word.slice(0, -1));
                        else keywordsToMatch.add(word + 's');
                    });
                const itemName = (item.name || '').toLowerCase();
                const itemDesc = (item.description || '').toLowerCase();
                matchesCategory = Array.from(keywordsToMatch).some(keyword => 
                    itemName.includes(keyword) || 
                    itemDesc.includes(keyword) ||
                    holidayVal.includes(keyword) ||
                    seasonVal.includes(keyword) ||
                    keywordsVal.includes(keyword)
                );
            }
        }
        let matchesSearch = true;
        if (searchQuery) {
             const search = searchQuery.toLowerCase();
             const itemName = (item.customName || item.name || '').toLowerCase();
             const itemDesc = (item.description || '').toLowerCase();
             const itemMfgPart = item.type === 'bundle' ? '' : (item.manufacturerPartNumber || item.sku || '').toLowerCase();
             
             // Also search against category names for products/components
             const itemCat = (item.category || '').toLowerCase();
             
             matchesSearch = itemName.includes(search) || itemDesc.includes(search) || itemMfgPart.includes(search) || itemCat.includes(search);
        }
        return matchesCategory && matchesSearch;
    });

    // Sorting Logic
    const sortedItems = React.useMemo(() => {
        const items = viewMode === 'component-list' ? filteredComponents : [...filteredBundles, ...filteredProducts];
        const sorted = [...items];
        
        switch (sortBy) {
            case 'price-low':
                sorted.sort((a, b) => ((a.price || a.bundlePrice) || 0) - ((b.price || b.bundlePrice) || 0));
                break;
            case 'price-high':
                sorted.sort((a, b) => ((b.price || b.bundlePrice) || 0) - ((a.price || a.bundlePrice) || 0));
                break;
            case 'name-asc':
                sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                break;
            case 'name-desc':
                sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
                break;
            case 'newest':
                 // If we had created_at, we would sort by it. Assuming default order is somewhat relevant or by ID
                break;
            default:
                break;
        }
        return sorted;
    }, [viewMode, filteredComponents, filteredProducts, filteredBundles, sortBy]);

    // Pagination Logic
    const totalItems = sortedItems.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const paginatedItems = sortedItems.slice(0, currentPage * itemsPerPage);

    // Early returns come AFTER hooks and logic
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mr-4"></div>
                <p className="text-muted-foreground">Loading storefront...</p>
            </div>
        );
    }

    if (storeNotFound) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 flex flex-col">
                <Header />
                <main className="flex-grow flex items-center justify-center">
                    <div className="text-center p-8">
                        <Icon name="SearchX" className="mx-auto mb-6 h-24 w-24 text-destructive" />
                        <h1 className="text-4xl font-bold text-foreground mb-4">Storefront Not Found</h1>
                        <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
                            The storefront you are looking for does not exist. It might have been moved or deleted.
                        </p>
                        <Button onClick={() => navigate('/model-catalog')} iconName="ChevronLeft" iconPosition="left">
                            Back to Main Catalog
                        </Button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (storeInactive) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 flex flex-col">
                <Header />
                <main className="flex-grow flex items-center justify-center">
                    <div className="text-center p-8 max-w-md mx-auto">
                        <div className="mx-auto mb-6 bg-red-50 p-4 rounded-full w-24 h-24 flex items-center justify-center text-red-600">
                          <Icon name="Lock" size={48} />
                        </div>
                        <h1 className="text-3xl font-extrabold text-foreground mb-4">Storefront Suspended</h1>
                        <p className="text-base text-muted-foreground mb-8">
                            This storefront is currently inactive or suspended by the administrator. Please check back later or contact customer support if you believe this is an error.
                        </p>
                        <Button onClick={() => navigate('/model-catalog')}>Back to Catalog</Button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="bg-destructive/10 border border-destructive rounded-lg p-4 text-destructive-foreground min-h-screen flex items-center justify-center">
                <Icon name="AlertCircle" className="inline mr-2" />
                {error}
            </div>
        );
    }

    // Show "Coming Soon" if the store is not active and the user is not the seller viewing in preview mode
    if (!storefrontData || (!storefrontData.storefrontActive && !canPreview)) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 flex flex-col">
                <Header />
                <main className="flex-grow flex items-center justify-center">
                    <div className="text-center p-8">
                        {/* Coming Soon Graphic */}
                        <img
                            src="/assets/images/comingsoonStorefront.jpg"
                            alt="Store Coming Soon"
                            className="mx-auto mb-6 w-full h-[500px] object-contain"
                        />
                        <h1 className="text-4xl font-bold text-foreground mb-4">
                            {storefrontData?.businessName ? `${storefrontData.businessName} is ` : ''}Coming Soon!
                        </h1>
                        <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
                            This seller is getting their storefront ready. Check back soon to see their collection of 3D models.
                        </p>
                        <Button onClick={() => navigate('/model-catalog')} iconName="ChevronLeft" iconPosition="left">
                            Back to Main Catalog
                        </Button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Helmet>
                <title>{storefrontData ? `${storefrontData.businessName || 'Store'}${activeCategory ? ` - ${activeCategory}` : ''} | 3D Click To Buy` : 'Storefront | 3D Click To Buy'}</title>
                <meta name="description" content={storefrontData?.description || `Browse ${activeCategory ? `${activeCategory} models and parts` : 'custom 3D models'} at ${storefrontData?.businessName || 'our store'}.`} />
                <link rel="canonical" href={`${window.location.origin}/store/${slug}${activeCategory ? `/${slugifyCategory(activeCategory)}` : ''}`} />
            </Helmet>
            <Header />
            {isPreview && (isStoreSuspended || isStorefrontClosed) && (
              <div className="bg-red-600 text-white font-bold text-xs py-3.5 px-4 text-center flex items-center justify-center gap-2 shadow-md uppercase tracking-wider select-none animate-in slide-in-from-top duration-300 border-b border-red-800">
                <Icon name="AlertCircle" size={16} className="text-white animate-pulse" />
                <span>PREVIEW MODE: This storefront is currently Temporarily Suspended or Inactive. The general public cannot access your store. This preview is visible only to you.</span>
              </div>
            )}
            <main>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Banner */}
                {storefrontData.storefrontBannerUrl && (
                    <div className="mb-8"> {/* Add margin bottom for spacing */}
                        <img
                            src={storefrontData.storefrontBannerUrl.startsWith('http') ? storefrontData.storefrontBannerUrl : imageUploadService.getPublicUrl(storefrontData.storefrontBannerUrl, 'storefront-banners')}
                            alt={`${storefrontData.businessName || 'My Store'} Banner`}
                            className="block mx-auto h-auto max-w-full object-contain rounded-md shadow-md"
                        />
                        <div className="text-center mt-2 text-2xl font-bold text-foreground">
                            {storefrontData.businessName || 'My Store'}
                        </div>
                    </div>
                )}

                    {/* Specialized Wrap Shop Storefront Portal */}
                    {isWrapShopSeller && (
                        <div className="mb-12">
                            <div className="bg-indigo-600/10 border border-indigo-500/20 rounded-2xl p-6 text-center max-w-4xl mx-auto mb-10 shadow-sm">
                                <Icon name="Palette" size={32} className="text-indigo-600 mx-auto mb-2.5 animate-pulse-soft" />
                                <h3 className="text-lg font-black uppercase text-indigo-800 tracking-wide">Authorized 3D Wrap & Livery Customization Center</h3>
                                <p className="text-sm text-slate-600 max-w-2xl mx-auto mt-1 leading-relaxed">
                                    Browse our portfolio of custom 3D wrap projects. Click on any design below to launch the interactive, full-screen customer 3D Wrap Preview.
                                </p>
                            </div>

                            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 border-b border-border pb-4">
                                <h2 className="text-xl font-black uppercase text-foreground flex items-center gap-1.5">
                                    <Icon name="Sparkles" size={18} className="text-indigo-600" />
                                    Custom Wrap Designs ({sellerWraps.length})
                                </h2>
                            </div>

                            {sellerWraps.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 text-left">
                                    {sellerWraps.map((w) => {
                                        const thumb = w.product?.thumbnail_path 
                                            ? (w.product.thumbnail_path.startsWith('http') ? w.product.thumbnail_path : imageUploadService.getPublicUrl(w.product.thumbnail_path, 'product-thumbnails'))
                                            : null;
                                        return (
                                            <div key={w.id} className="bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col justify-between">
                                                <div className="relative aspect-[4/3] bg-muted flex items-center justify-center overflow-hidden">
                                                    {thumb ? (
                                                        <img src={thumb} alt={w.config_name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <Icon name="Palette" size={40} className="text-muted-foreground/30 animate-pulse-soft" />
                                                    )}
                                                    <span className="absolute top-3 right-3 bg-indigo-600 text-white font-black text-[9px] uppercase tracking-wider px-2 py-0.5 rounded shadow">Custom Wrap</span>
                                                </div>
                                                <div className="p-4 flex-1 flex flex-col justify-between gap-3">
                                                    <div>
                                                        <h4 className="font-extrabold text-sm text-foreground truncate uppercase">{w.config_name}</h4>
                                                        <p className="text-xs text-muted-foreground mt-0.5 truncate flex items-center gap-1">
                                                            <Icon name="Box" size={12} />
                                                            Base Vehicle: {w.product?.name || 'Standard Model'}
                                                        </p>
                                                    </div>
                                                    <Button 
                                                        variant="default"
                                                        onClick={() => window.open(`/diagnostic-report?assemblyId=${w.id}&mode=preview`, '_blank')}
                                                        className="w-full text-xs font-black uppercase tracking-wider h-10 bg-indigo-600 hover:bg-indigo-500 text-white shadow-sm flex items-center justify-center gap-1.5"
                                                    >
                                                        <Icon name="Eye" size={13} />
                                                        View Interactive 3D Wrap
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-12 border border-dashed border-border rounded-2xl bg-muted/10 max-w-md mx-auto">
                                    <Icon name="Palette" size={28} className="mx-auto text-muted-foreground/30 mb-2" />
                                    <p className="text-sm font-bold text-muted-foreground">No wrap configurations published yet.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Specialized Repair Shop Storefront Portal */}
                    {isRepairShopSeller && (
                        <div className="mb-12">
                            <div className="bg-emerald-600/10 border border-emerald-500/20 rounded-2xl p-6 text-center max-w-4xl mx-auto mb-10 shadow-sm">
                                <Icon name="Wrench" size={32} className="text-emerald-600 mx-auto mb-2.5 animate-pulse-soft" />
                                <h3 className="text-lg font-black uppercase text-emerald-800 tracking-wide">Authorized Mechanical Repair & 3D Diagnostics Center</h3>
                                <p className="text-sm text-slate-600 max-w-2xl mx-auto mt-1 leading-relaxed">
                                    We utilize advanced interactive 3D WebGL computer diagnostic tracking and real-time client verification. 
                                    Bring your assembly to our facility for certified service, repairs, and automated part compatibility audits.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 text-left">
                                <div className="bg-card border border-border p-5 rounded-2xl space-y-2.5 hover:shadow-md transition-shadow">
                                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                                        <Icon name="Sparkles" size={20} />
                                    </div>
                                    <h4 className="font-extrabold text-sm uppercase text-foreground">Interactive 3D Diagnostics</h4>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        Technicians visually isolate damaged areas directly on a 3D digital-twin of your vehicle. Inspect repair records right from the 3D visualizer.
                                    </p>
                                </div>
                                <div className="bg-card border border-border p-5 rounded-2xl space-y-2.5 hover:shadow-md transition-shadow">
                                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                                        <Icon name="Award" size={20} />
                                    </div>
                                    <h4 className="font-extrabold text-sm uppercase text-foreground">Symmetrical Client Approvals</h4>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        Receive instantaneous digital estimates. Accept or decline individual lines, and complete secure Stripe invoice checkouts from any device.
                                    </p>
                                </div>
                                <div className="bg-card border border-border p-5 rounded-2xl space-y-2.5 hover:shadow-md transition-shadow">
                                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                                        <Icon name="ShieldCheck" size={20} />
                                    </div>
                                    <h4 className="font-extrabold text-sm uppercase text-foreground">Certified Replacement Parts</h4>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        We supply only manufacturer-certified parts. Integrated logic audits your vehicle serial number to guarantee 100% component compatibility.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {(!isWrapShopSeller || sellerProducts.length > 0) && (
                        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                            <h2 className="text-2xl font-bold text-foreground">Our Products</h2>
                            <div className="relative w-full md:w-72">
                                <Icon name="Search" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search store..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1); // Reset page on search
                                    }}
                                    className="pl-10 pr-4 py-2 w-full border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>
                        </div>
                    )}

                    {/* Category Tabs */}
                    {storefrontData.storefrontCategories && storefrontData.storefrontCategories.length > 0 && (
                        <div className="border-b border-border mb-6">
                            <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-thin pb-1" aria-label="Storefront Categories">
                                {/* All Category Button */}
                                <button
                                    onClick={() => handleCategorySelect('')} // Clear active category to show all
                                    className={`
                                        ${activeCategory === ''
                                            ? 'border-primary text-primary font-bold'
                                            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                        }
                                        whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                                    `}
                                >
                                    All
                                </button>
                                {storefrontData.storefrontCategories.map((category) => (
                                    <button
                                        key={category}
                                        onClick={() => handleCategorySelect(category)}
                                        className={`
                                            ${activeCategory === category
                                                ? 'border-primary text-primary font-bold'
                                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                            }
                                            whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                                        `}
                                    >
                                        {category}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    )}

                    {/* View Controls */}
                    <ViewControls
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        sortBy={sortBy}
                        onSortChange={setSortBy}
                        itemsPerPage={itemsPerPage}
                        onItemsPerPageChange={setItemsPerPage}
                        totalItems={totalItems}
                        itemType={viewMode === 'component-list' ? 'components' : 'models'}
                        selectedItems={selectedModels}
                        onSelectAll={handleSelectAll}
                        onClearSelection={() => setSelectedModels([])}
                        onBulkAction={handleBulkAction}
                    />

                    {/* Products Display */}
                    {loadingComponents ? (
                         <div className="flex justify-center py-12">
                             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                         </div>
                    ) : paginatedItems.length > 0 ? (
                        <>
                            {viewMode === 'component-list' ? (
                               <ComponentListView
                                   components={paginatedItems}
                                   onAddToCart={(component) => handleAddToCart(component, true)}
                                   isAuthenticated={isAuthenticated}
                                   onAuthRequired={handleAuthRequired}
                                   onViewDetails={(component) => setSelectedDetailComponent(component)}
                                   isStorefront={true}
                                   cardLayout={publishedLayout}
                               />
                            ) : (
                                <div className={`
                                    ${viewMode === 'grid' 
                                        ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6' 
                                        : 'space-y-4' // List/Row view
                                    }
                                `}>
                                    {paginatedItems.map(item => {
                                        if (item.type === 'bundle') {
                                            return (
                                                <div 
                                                    key={item.id} 
                                                    className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-soft transition-all duration-300 flex flex-col h-full cursor-pointer group"
                                                    onClick={() => setSelectedBundle(item)}
                                                >
                                                    <div className="relative aspect-square bg-muted">
                                                        {item.image ? (
                                                            <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                                                <Icon name="Package" size={48} />
                                                            </div>
                                                        )}
                                                        <div className="absolute top-2 left-2 bg-accent text-accent-foreground text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-sm">
                                                            <Icon name="Layers" size={12} /> BUNDLE
                                                        </div>
                                                        {item.savings > 0 && (
                                                            <div className="absolute bottom-2 right-2 bg-success text-success-foreground text-xs font-bold px-2 py-1 rounded shadow-sm">
                                                                Save ${item.savings.toFixed(2)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="p-4 flex flex-col flex-grow">
                                                        <h3 className="font-semibold text-foreground line-clamp-2 mb-1">{item.name}</h3>
                                                        <p className="text-xs text-muted-foreground mb-3">{item.itemCount} Items Included</p>
                                                        <div className="mt-auto flex items-center justify-between">
                                                            <div>
                                                                <span className="font-bold text-lg">${item.bundlePrice?.toFixed(2)}</span>
                                                                {item.originalPrice > item.bundlePrice && (
                                                                    <span className="text-xs text-muted-foreground line-through ml-2">${item.originalPrice?.toFixed(2)}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <ModelCard
                                                key={item.id}
                                                model={item}
                                                viewMode={viewMode} // Pass viewMode to ModelCard
                                                cardLayout={publishedLayout}
                                                shopId={storefrontData?.id}
                                                isAuthenticated={isAuthenticated}
                                                onAuthRequired={handleAuthRequired}
                                                onQuickPreview={() => navigate(`/3d-model-viewer?id=${item.id}&shopId=${storefrontData?.id}`)}
                                                isSelected={selectedModels.includes(item.id)}
                                                onSelect={handleSelectModel}
                                                onAddToCart={(id) => handleAddToCart(id, false)}
                                            />
                                        );
                                    })}
                                </div>
                            )}

                            {/* Load More Button */}
                            {currentPage < totalPages && (
                                <div className="flex justify-center mt-8">
                                    <Button
                                        variant="outline"
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                        iconName="ChevronDown"
                                        className="w-full max-w-xs"
                                    >
                                        Load More...
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-10">
                            <p className="text-muted-foreground">
                                {viewMode === 'component-list' 
                                    ? 'No components found for this store.' 
                                    : 'No models found in this category.'}
                            </p>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
            
            {/* Render the Modals */}
            {selectedBundle && (
                <BundlePreviewModal
                    bundle={selectedBundle}
                    onClose={() => setSelectedBundle(null)}
                    onAddBundle={async (bundleId) => {
                        if (!isAuthenticated) {
                            handleAuthRequired();
                            return;
                        }
                        try {
                            await bundleService.addBundleToCart(user?.id, bundleId);
                            alert('Bundle added to cart!');
                            setSelectedBundle(null);
                        } catch (error) {
                            console.error('Failed to add bundle to cart:', error);
                            alert(error.message || 'Failed to add bundle to cart');
                        }
                    }}
                />
            )}

            <ComponentDetailModal
                component={selectedDetailComponent}
                isOpen={!!selectedDetailComponent}
                onClose={() => setSelectedDetailComponent(null)}
                onAddToCart={(component) => handleAddToCart(component, true)}
                isAuthenticated={isAuthenticated}
                onAuthRequired={handleAuthRequired}
                storefrontOnly={true} // Add storefrontOnly prop to indicate it shouldn't fetch other sellers
            />

            <LoginSignupModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                onSuccess={() => {
                    setShowAuthModal(false);
                }}
            />
        </div>
    );
};

export default PublicStorefront;